import { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { ShieldCheck, ShieldAlert, CreditCard, Receipt, Download, ExternalLink, FileCheck, Search, X, AlertCircle } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { formatDateTime, formatCHF } from "@/lib/format";
import AdminDriverCreateDialog from "@/components/admin/AdminDriverCreateDialog";

const VEHICLE_LABELS = { moto: "Moto", scooter: "Scooter", auto: "Auto", auto_grande: "Auto grande", furgone: "Furgone" };

const DOCS_PILL = {
  missing: { label: "Doc mancanti", cls: "bg-red-50 text-red-700" },
  incomplete: { label: "Doc incompleti", cls: "bg-red-50 text-red-700" },
  pending: { label: "Doc in verifica", cls: "bg-amber-50 text-amber-700" },
  verified_basic: { label: "Verificato base", cls: "bg-emerald-50 text-emerald-700" },
  verified_full: { label: "Verificato completo", cls: "bg-emerald-100 text-emerald-800" },
};

// Phase 43.19.1 — Filtri operativi propagati dal Centro Operativo Admin
const OPS_FILTERS = {
  verification_pending: {
    label: "Driver da verificare (KYC pending)",
    apply: (d) => !((d.driver_info || {}).compliance_verified_at),
  },
  compliance_expired: {
    label: "Driver con documenti scaduti o in scadenza (entro 30 giorni)",
    apply: (d) => {
      const info = d.driver_info || {};
      const today = new Date().toISOString().slice(0, 10);
      const limit = new Date(Date.now() + 30 * 86400000).toISOString().slice(0, 10);
      const lic = info.license_expiry;
      const ins = info.insurance_expiry;
      return (lic && lic <= limit) || (ins && ins <= limit);
    },
  },
};

export default function AdminDrivers() {
  const [searchParams, setSearchParams] = useSearchParams();
  const opsFilter = searchParams.get("filter") || "";
  const opsPreset = OPS_FILTERS[opsFilter] || null;

  const [drivers, setDrivers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(null);
  const [createOpen, setCreateOpen] = useState(false);
  const [search, setSearch] = useState("");
  const [taxByDriver, setTaxByDriver] = useState({});
  const [taxLoading, setTaxLoading] = useState(false);
  // Phase 44.31.18 — toggle "Includi archiviati" (default OFF)
  const [includeArchived, setIncludeArchived] = useState(searchParams.get("archived") === "1");
  const currentYear = new Date().getFullYear();

  const clearOpsFilter = () => {
    const next = new URLSearchParams(searchParams);
    next.delete("filter");
    setSearchParams(next, { replace: true });
  };

  const load = async () => {
    setLoading(true);
    try {
      const params = { role: "driver" };
      if (includeArchived) params.include_archived = "true";
      const { data } = await api.get("/admin/users", { params });
      setDrivers(data);
    } catch (e) {
      if (e.response?.status !== 403) {
        toast.error(formatApiError(e.response?.data?.detail) || e.message);
      }
      setDrivers([]);
    } finally {
      setLoading(false);
    }
  };

  // Lazy-fetch tax summaries per driver in parallel after the list is loaded
  useEffect(() => {
    if (drivers.length === 0) return;
    let cancel = false;
    setTaxLoading(true);
    Promise.allSettled(
      drivers.map((d) =>
        api.get(`/admin/drivers/${d.id}/tax-summary`, { params: { year: currentYear } })
          .then((r) => [d.id, r.data])
          .catch(() => [d.id, null])
      )
    ).then((results) => {
      if (cancel) return;
      const out = {};
      for (const r of results) {
        if (r.status === "fulfilled" && r.value[1]) out[r.value[0]] = r.value[1];
      }
      setTaxByDriver(out);
      setTaxLoading(false);
    });
    return () => { cancel = true; };
  }, [drivers, currentYear]);

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [includeArchived]);

  // Phase 44.31.18 — sync toggle to URL
  useEffect(() => {
    const sp = new URLSearchParams(searchParams);
    if (includeArchived) sp.set("archived", "1"); else sp.delete("archived");
    setSearchParams(sp, { replace: true });
    // eslint-disable-next-line
  }, [includeArchived]);

  const setVerified = async (id, value) => {
    setBusy(id + "v");
    try {
      await api.patch(`/admin/drivers/${id}/verify`, { is_verified: value });
      toast.success(value ? "Driver verificato" : "Verifica revocata");
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    } finally {
      setBusy(null);
    }
  };

  const verifyPayout = async (id, value) => {
    setBusy(id + "p");
    try {
      await api.patch(`/admin/drivers/${id}/payout-verify`, { verified: value });
      toast.success(value ? "IBAN verificato" : "Verifica IBAN revocata");
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    } finally {
      setBusy(null);
    }
  };

  const exportTaxCsv = async () => {
    try {
      const res = await api.get("/admin/drivers/tax-export.csv", {
        params: { year: currentYear }, responseType: "blob",
      });
      const blob = new Blob([res.data], { type: "text/csv" });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `dapnow_drivers_tax_${currentYear}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success("Export storico CSV avviato");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    }
  };

  const enriched = useMemo(() => drivers.map((d) => {
    const tax = taxByDriver[d.id] || null;
    return { ...d, tax };
  }), [drivers, taxByDriver]);

  const visible = useMemo(() => {
    let list = enriched;
    if (opsPreset) list = list.filter(opsPreset.apply);  // Phase 43.19.1
    const q = search.trim().toLowerCase();
    if (q) {
      list = list.filter((d) => {
        const info = d.driver_info || {};
        return (
          (d.name || "").toLowerCase().includes(q) ||
          (d.email || "").toLowerCase().includes(q) ||
          (d.phone || "").toLowerCase().includes(q) ||
          (info.vehicle_brand || "").toLowerCase().includes(q) ||
          (info.vehicle_model || "").toLowerCase().includes(q) ||
          (info.vehicle_plate || "").toLowerCase().includes(q) ||
          (info.license_number || "").toLowerCase().includes(q)
        );
      });
    }
    return list;
  }, [enriched, search, opsPreset]);


  return (
    <div className="space-y-5" data-testid="admin-drivers-page">
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <p className="overline">Onboarding</p>
          <h1 className="font-display text-3xl font-extrabold tracking-tighter mt-1">
            {opsPreset ? opsPreset.label : "Trasportatori"}
          </h1>
          {opsPreset && (
            <p className="text-sm text-dapnow-muted mt-1">
              {visible.length} driver corrispondent{visible.length === 1 ? "e" : "i"} al filtro operativo.
            </p>
          )}
        </div>
        <div className="flex items-center flex-wrap gap-2">
          <button onClick={() => setCreateOpen(true)} className="btn-primary text-xs h-10 px-3" data-testid="admin-driver-create">Nuovo driver interno</button>
          <label className="inline-flex items-center gap-2 text-xs text-dapnow-muted cursor-pointer select-none">
            <input
              type="checkbox"
              checked={includeArchived}
              onChange={(e) => setIncludeArchived(e.target.checked)}
              data-testid="admin-drivers-include-archived"
              className="h-4 w-4 rounded border-dapnow-line accent-dapnow-blue"
            />
            Includi archiviati
          </label>
          <button onClick={exportTaxCsv} data-testid="admin-tax-export-csv" className="btn-primary text-xs h-10 px-3">
            <Download className="h-3.5 w-3.5 mr-1" /> Export storico {currentYear}
          </button>
        </div>
      </div>

      {opsPreset && (
        <div
          data-testid="admin-drivers-ops-filter-banner"
          className="flex items-center justify-between gap-3 rounded-2xl bg-amber-50 border border-amber-200 px-4 py-3"
        >
          <div className="flex items-center gap-2 min-w-0">
            <AlertCircle className="h-5 w-5 text-amber-700 flex-shrink-0" />
            <p className="text-sm text-amber-900 truncate">
              <strong>Filtro operativo attivo:</strong> {opsPreset.label}.
            </p>
          </div>
          <button
            onClick={clearOpsFilter}
            data-testid="admin-drivers-ops-filter-clear"
            className="inline-flex items-center gap-1 rounded-lg bg-white border border-amber-300 px-3 h-8 text-xs font-semibold hover:border-amber-500 flex-shrink-0"
          >
            <X className="h-3.5 w-3.5" /> Rimuovi filtro
          </button>
        </div>
      )}

      <div className="relative">
        <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-dapnow-muted" />
        <input
          type="search"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Cerca per nome, email, telefono, marca/modello, targa, patente…"
          data-testid="admin-drivers-search"
          className="input-tall w-full pl-10 pr-10"
        />
        {search && (
          <button
            type="button"
            onClick={() => setSearch("")}
            data-testid="admin-drivers-search-clear"
            aria-label="Pulisci"
            className="absolute right-2 top-1/2 -translate-y-1/2 h-8 w-8 inline-flex items-center justify-center rounded-lg hover:bg-dapnow-bg"
          >
            <X className="h-4 w-4" />
          </button>
        )}
      </div>

      {loading ? (
        <div className="card-soft p-10 text-center text-dapnow-muted">Caricamento…</div>
      ) : visible.length === 0 ? (
        <div className="card-soft p-10 text-center text-dapnow-muted">
          {search ? "Nessun driver trovato per la ricerca." : "Nessun trasportatore registrato."}
        </div>
      ) : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {visible.map((d) => {
            const info = d.driver_info || {};
            const v = info.is_verified;
            const payoutStatus = info.payout_status || (info.payout_iban ? "pending" : "missing");
            const tax = d.tax;
            const annualNet = tax?.annual?.net_payout || 0;
            const annualOrders = tax?.annual?.orders || 0;
            const taxAccepted = !!d.tax_agreement_accepted;
            return (
              <div key={d.id} className={`card-soft p-5 ${d.archived ? "opacity-60" : ""}`} data-testid={`admin-driver-${d.id}`}>
                <div className="flex items-center justify-between">
                  <div className="min-w-0">
                    <p className="overline">Driver</p>
                    <h3 className="font-display text-lg font-semibold tracking-tight mt-0.5">
                      {d.name}
                      {d.archived && (
                        <span
                          className="ml-2 inline-flex items-center pill bg-zinc-200 text-zinc-700 text-[9px] uppercase tracking-wider align-middle"
                          title={`${d.archived_reason || "archiviato"}${d.archived_at ? " · " + formatDateTime(d.archived_at) : ""}${d.archived_batch ? " · " + d.archived_batch : ""}`}
                          data-testid={`admin-driver-archived-badge-${d.id}`}
                        >
                          Archiviato
                        </span>
                      )}
                    </h3>
                    <p className="text-xs text-dapnow-muted">{d.email}</p>
                  </div>
                  {v ? <ShieldCheck className="h-5 w-5 text-emerald-600" /> : <ShieldAlert className="h-5 w-5 text-amber-600" />}
                </div>
                {(() => {
                  const docsStatus = info.documents_status || "missing";
                  const pill = DOCS_PILL[docsStatus] || DOCS_PILL.missing;
                  return (
                    <span className={`mt-2 inline-flex pill text-[10px] ${pill.cls}`} data-testid={`admin-driver-docs-pill-${d.id}`}>
                      <FileCheck className="h-3 w-3 mr-1" /> {pill.label}
                    </span>
                  );
                })()}
                <dl className="mt-4 grid grid-cols-2 gap-2 text-sm">
                  <div><dt className="overline text-[10px]">Veicolo</dt><dd>{info.vehicle_type ? VEHICLE_LABELS[info.vehicle_type] : "—"}</dd></div>
                  <div><dt className="overline text-[10px]">Targa</dt><dd>{info.vehicle_plate || "—"}</dd></div>
                  <div className="col-span-2"><dt className="overline text-[10px]">Marca/Modello</dt><dd>{[info.vehicle_brand, info.vehicle_model].filter(Boolean).join(" ") || "—"}</dd></div>
                  <div className="col-span-2"><dt className="overline text-[10px]">Patente</dt><dd>{info.license_number || "—"}</dd></div>
                  <div className="col-span-2 border-t border-dapnow-line pt-2 mt-1">
                    <dt className="overline text-[10px] inline-flex items-center gap-1"><CreditCard className="h-3 w-3" /> IBAN</dt>
                    <dd className="font-mono text-xs">{info.payout_iban ? `${info.payout_iban.slice(0, 4)} ••• ${info.payout_iban.slice(-4)}` : "Non inserito"}</dd>
                    <span className={`mt-1 inline-flex pill text-[10px] ${payoutStatus === "verified" ? "bg-emerald-50 text-emerald-700" : payoutStatus === "pending" ? "bg-amber-50 text-amber-700" : "bg-red-50 text-red-700"}`}>
                      {payoutStatus === "verified" ? "IBAN verificato" : payoutStatus === "pending" ? "In verifica" : "Mancante"}
                    </span>
                  </div>
                  <div className="col-span-2 border-t border-dapnow-line pt-2 mt-1" data-testid={`admin-driver-tax-${d.id}`}>
                    <dt className="overline text-[10px] inline-flex items-center gap-1"><Receipt className="h-3 w-3" /> Storico da riconciliare {currentYear}</dt>
                    <dd className="text-xs mt-1 space-y-1">
                      <div className="flex justify-between">
                        <span className="text-dapnow-muted">Netto registrato annuo</span>
                        <span className="font-semibold">{taxLoading && !tax ? "…" : formatCHF(annualNet)}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-dapnow-muted">Consegne</span>
                        <span className="font-semibold">{annualOrders}</span>
                      </div>
                      <div className="flex flex-wrap gap-1 mt-1.5">
                        <span className={`inline-flex pill text-[10px] ${taxAccepted ? "bg-emerald-50 text-emerald-700" : "bg-red-50 text-red-700"}`}>
                          {taxAccepted ? "Dichiarazione storica presente" : "Dichiarazione storica non presente"}
                        </span>

                      </div>
                    </dd>
                  </div>
                  <div className="col-span-2 text-xs text-dapnow-muted">{formatDateTime(d.created_at)}</div>
                </dl>
                <div className="mt-4 grid grid-cols-2 gap-2">
                  {v ? (
                    <button onClick={() => setVerified(d.id, false)} disabled={busy === d.id + "v"} data-testid={`admin-driver-revoke-${d.id}`} className="btn-ghost text-xs h-10 px-3">
                      Revoca docs
                    </button>
                  ) : (
                    <button onClick={() => setVerified(d.id, true)} disabled={busy === d.id + "v"} data-testid={`admin-driver-verify-${d.id}`} className="btn-primary text-xs h-10 px-3">
                      <ShieldCheck className="h-3.5 w-3.5 mr-1" /> Verifica docs
                    </button>
                  )}
                  {info.payout_iban ? (
                    payoutStatus === "verified" ? (
                      <button onClick={() => verifyPayout(d.id, false)} disabled={busy === d.id + "p"} data-testid={`admin-payout-revoke-${d.id}`} className="btn-ghost text-xs h-10 px-3">
                        Revoca IBAN
                      </button>
                    ) : (
                      <button onClick={() => verifyPayout(d.id, true)} disabled={busy === d.id + "p"} data-testid={`admin-payout-verify-${d.id}`} className="btn-primary text-xs h-10 px-3">
                        <CreditCard className="h-3.5 w-3.5 mr-1" /> Verifica IBAN
                      </button>
                    )
                  ) : (
                    <span className="text-xs text-dapnow-muted text-center py-2">No IBAN</span>
                  )}
                </div>
                <Link
                  to={`/admin/drivers/${d.id}`}
                  data-testid={`admin-driver-open-${d.id}`}
                  className="mt-3 inline-flex items-center justify-center gap-1.5 w-full h-10 rounded-xl border border-dapnow-blue text-dapnow-blue bg-white text-sm font-semibold hover:bg-dapnow-blue-soft transition"
                >
                  <ExternalLink className="h-4 w-4" /> Apri scheda driver
                </Link>
                <label className="block mt-3 text-xs text-dapnow-muted">ID per autorizzazione flotta
                  <input readOnly value={d.id} onFocus={(e) => e.target.select()} className="mt-1 w-full min-w-0 rounded border border-dapnow-line p-2 text-xs" aria-label={`ID driver ${d.name}`} />
                </label>
              </div>
            );
          })}
        </div>
      )}
      <AdminDriverCreateDialog open={createOpen} onClose={() => setCreateOpen(false)} onCreated={load} />
    </div>
  );
}
